/**
 * PII Redaction Utilities
 * 
 * GDPR-compliant utilities for pseudonymizing and redacting Personally Identifiable Information (PII)
 * from telemetry data before dispatch to PostHog/Sentry.
 * 
 * Fixes F002: Uses HMAC-SHA256 with secret salt (rainbow table protection)
 */

/**
 * Get the secret salt for HMAC hashing
 * WARNING: Must be kept secret and rotated periodically
 */
function getHashSecret(): string {
  const secret = import.meta.env.VITE_TELEMETRY_HASH_SECRET;
  
  if (!secret || secret === 'CHANGE-ME-IN-PRODUCTION') {
    console.warn('[Telemetry] VITE_TELEMETRY_HASH_SECRET not set or using default - user ID hashing is insecure!');
    if (!import.meta.env.DEV) {
      console.error('[Telemetry] CRITICAL: Production deployment MUST set VITE_TELEMETRY_HASH_SECRET');
    }
  }
  
  return secret || 'CHANGE-ME-IN-PRODUCTION';
}

/**
 * Hash user ID using HMAC-SHA256 with secret salt
 * 
 * Returns 16-character hash (64 bits) providing adequate collision resistance
 * while maintaining user privacy.
 * 
 * Fixes F002: HMAC-SHA256 prevents rainbow table attacks
 * 
 * @param userId - Supabase user ID (UUID format)
 * @returns 16-character hex hash
 */
export async function hashUserId(userId: string): Promise<string> {
  if (!userId) {
    return 'anonymous';
  }

  const secret = getHashSecret();
  const encoder = new TextEncoder();
  
  // Import HMAC key
  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  
  // Sign the user ID
  const signature = await crypto.subtle.sign(
    'HMAC',
    key,
    encoder.encode(userId)
  );
  
  // Convert to hex and take first 16 characters (64 bits)
  const hashArray = Array.from(new Uint8Array(signature));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  return hashHex.substring(0, 16);
}

/**
 * Anonymize GPS coordinate by rounding to ~10km grid
 * 
 * Reduces precision to 0.1° (approximately 10km at equator)
 * sufficient for city/region analytics while protecting precise location.
 * 
 * @param coord - Latitude or longitude (decimal degrees)
 * @returns Rounded coordinate (1 decimal place)
 */
export function anonymizeCoordinate(coord: number): number {
  // Round to 0.1° precision (~10km grid)
  return Math.round(coord * 10) / 10;
}

/**
 * Sanitize error message by removing PII patterns
 * 
 * Removes:
 * - Email addresses
 * - Bearer tokens
 * - Precise GPS coordinates
 * - UUIDs (potential user IDs)
 * 
 * @param error - Error object or message
 * @returns Sanitized error message
 */
export function sanitizeError(error: Error | string): string {
  const message = typeof error === 'string' ? error : error.message;
  
  return message
    // Remove email addresses
    .replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[EMAIL]')
    // Remove Bearer tokens
    .replace(/Bearer\s+[A-Za-z0-9\-._~+/]+=*/g, '[TOKEN]')
    // Remove precise coordinates (>3 decimal places)
    .replace(/\d+\.\d{4,}/g, '[COORD]')
    // Remove UUIDs
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '[UUID]');
}

/**
 * Sanitize URL by removing query parameters and fragments
 * Already implemented in telemetry.ts, but duplicated here for consistency
 * 
 * @param url - URL string
 * @returns Sanitized URL (host + pathname only)
 */
export function sanitizeUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    // Keep only host and pathname, strip query params and hash
    return `${parsed.host}${parsed.pathname}`;
  } catch {
    // If URL parsing fails, redact everything after the path
    return url.split('?')[0].split('#')[0];
  }
}

/**
 * Check if a value contains PII patterns
 * 
 * Used by PII guard to validate event properties before dispatch
 * 
 * @param value - Value to check (will be stringified)
 * @returns true if PII detected
 */
export function containsPII(value: any): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  
  const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
  
  const piiPatterns = [
    /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, // Email
    /Bearer\s+[A-Za-z0-9\-._~+/]+=*/g, // Bearer token
    /\d+\.\d{4,}/g, // Precise coordinate (>3 decimal places)
  ];
  
  return piiPatterns.some(pattern => pattern.test(stringValue));
}

/**
 * Redact PII from an object's values
 * 
 * Recursively walks an object and redacts any PII found in string values.
 * Used as a safety net for event properties.
 * 
 * @param obj - Object to redact
 * @returns Object with PII redacted
 */
export function redactPII(obj: Record<string, any>): Record<string, any> {
  const redacted: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(obj)) {
    if (value === null || value === undefined) {
      redacted[key] = value;
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      redacted[key] = redactPII(value);
    } else if (typeof value === 'string') {
      redacted[key] = sanitizeError(value);
    } else {
      redacted[key] = value;
    }
  }
  
  return redacted;
}

/**
 * Get application version from package.json
 * Used for Sentry release tagging
 */
export function getAppVersion(): string {
  // Vite doesn't expose package.json by default
  // We'll need to set this via environment variable or import.meta
  return import.meta.env.VITE_APP_VERSION || '0.0.0-dev';
}

/**
 * Get platform identifier
 * 
 * @returns 'ios' | 'android' | 'web'
 */
export function getPlatform(): 'ios' | 'android' | 'web' {
  // Check if running in Capacitor
  if (typeof window !== 'undefined') {
    const userAgent = window.navigator.userAgent.toLowerCase();
    
    // Capacitor sets these identifiers
    if (userAgent.includes('capacitor')) {
      if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
        return 'ios';
      }
      if (userAgent.includes('android')) {
        return 'android';
      }
    }
    
    // Fallback to mobile detection
    if (/iphone|ipad|ipod/.test(userAgent)) {
      return 'ios';
    }
    if (/android/.test(userAgent)) {
      return 'android';
    }
  }
  
  return 'web';
}
